import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { ethers } from 'ethers';
import { useNetwork } from './NetworkContext';
import { NetworkConfig } from '../config/networks';

interface EscrowContextType {
  contractAddress: string;
  setContractAddress: (address: string) => void;
  clearContractAddress: () => void;
  isValidAddress: (address: string) => boolean;
  isAddressSet: boolean;
}

const EscrowContext = createContext<EscrowContextType | undefined>(undefined);

export const useEscrow = () => {
  const context = useContext(EscrowContext);
  if (context === undefined) {
    throw new Error('useEscrow must be used within an EscrowProvider');
  }
  return context;
};

interface EscrowProviderProps {
  children: ReactNode;
}

export const EscrowProvider: React.FC<EscrowProviderProps> = ({ children }) => {
  const [contractAddress, setContractAddressState] = useState<string>('');
  const { currentNetwork } = useNetwork();
  const previousNetworkRef = useRef<NetworkConfig | null>(null);

  // Load contract address from localStorage on mount
  useEffect(() => {
    const savedAddress = localStorage.getItem('escrowContractAddress');
    if (savedAddress) {
      setContractAddressState(savedAddress);
    }
  }, []);

  // Clear contract address when network changes
  useEffect(() => {
    if (currentNetwork && previousNetworkRef.current && 
        currentNetwork.chainId !== previousNetworkRef.current.chainId && 
        contractAddress) {
      // Clear the contract address when network actually changes
      setContractAddressState('');
      localStorage.removeItem('escrowContractAddress');
    }
    // Update the previous network reference
    previousNetworkRef.current = currentNetwork;
  }, [currentNetwork, contractAddress]);

  const setContractAddress = useCallback((address: string) => {
    setContractAddressState(address);
    localStorage.setItem('escrowContractAddress', address);
  }, []);

  const clearContractAddress = useCallback(() => {
    setContractAddressState('');
    localStorage.removeItem('escrowContractAddress');
  }, []);

  const isValidAddress = useCallback((address: string) => {
    return ethers.isAddress(address);
  }, []);

  const isAddressSet = contractAddress !== '';

  const value: EscrowContextType = {
    contractAddress,
    setContractAddress,
    clearContractAddress,
    isValidAddress,
    isAddressSet,
  };

  return (
    <EscrowContext.Provider value={value}>
      {children}
    </EscrowContext.Provider>
  );
};
